package com.schoolconnect.app.feature.common

import androidx.compose.runtime.compositionLocalOf
import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow

val LocalAppLogoutInProgress = compositionLocalOf { false }

data class ParentChildContext(
    val children: List<ParentAttendanceChild> = emptyList(),
    val selectedChildId: String = "",
    val onSelected: (String) -> Unit = {}
)

val LocalParentChildContext = compositionLocalOf { ParentChildContext() }

/**
 * The authenticated shell is shared by Home and standalone rail destinations.
 * Keep the profile resolved by Home available when a feature route replaces it.
 */
data class ShellProfile(
    val fullName: String = "",
    val email: String = "",
    val mobileNumber: String = "",
    val employeeId: String = "",
    val schoolRole: String = "",
    val authRole: String = "",
    val mobileVerified: Boolean? = null,
    val emailVerified: Boolean? = null,
    val profileImageUrl: String = "",
    val pushNotificationsEnabled: Boolean = true,
    val emailNotificationsEnabled: Boolean = true
)

object ShellProfileStore {
    private val _profile = MutableStateFlow(ShellProfile())
    val profile = _profile.asStateFlow()

    fun update(
        fullName: String,
        email: String,
        profileImageUrl: String? = null,
        pushNotificationsEnabled: Boolean? = null,
        emailNotificationsEnabled: Boolean? = null,
        mobileNumber: String? = null,
        employeeId: String? = null,
        schoolRole: String? = null,
        authRole: String? = null,
        mobileVerified: Boolean? = null,
        emailVerified: Boolean? = null
    ) {
        val normalizedName = fullName.trim()
        val normalizedEmail = email.trim()
        if (normalizedName.isBlank() && normalizedEmail.isBlank()) return
        val current = _profile.value
        _profile.value = current.copy(
            fullName = normalizedName.ifBlank { current.fullName },
            email = normalizedEmail.ifBlank { current.email },
            // Null = leave unchanged; non-null (including blank) replaces so role
            // switches can clear staff metadata from a previous context.
            mobileNumber = mobileNumber?.trim() ?: current.mobileNumber,
            employeeId = employeeId?.trim() ?: current.employeeId,
            schoolRole = schoolRole?.trim() ?: current.schoolRole,
            authRole = authRole?.trim() ?: current.authRole,
            mobileVerified = mobileVerified ?: current.mobileVerified,
            emailVerified = emailVerified ?: current.emailVerified,
            profileImageUrl = profileImageUrl?.trim()?.takeIf { it.isNotBlank() } ?: current.profileImageUrl,
            pushNotificationsEnabled = pushNotificationsEnabled ?: current.pushNotificationsEnabled,
            emailNotificationsEnabled = emailNotificationsEnabled ?: current.emailNotificationsEnabled
        )
    }

    fun clearStaffFields() {
        _profile.value = _profile.value.copy(
            employeeId = "",
            schoolRole = "",
            authRole = ""
        )
    }

    fun clear() {
        _profile.value = ShellProfile()
    }
}

/** Shared parent selection survives feature route changes in the authenticated shell. */
object ParentChildSelectionStore {
    private val _children = MutableStateFlow<List<ParentAttendanceChild>>(emptyList())
    val children = _children.asStateFlow()
    private val _selectedChildId = MutableStateFlow("")
    val selectedChildId = _selectedChildId.asStateFlow()

    fun setChildren(children: List<ParentAttendanceChild>) {
        _children.value = children.distinctBy { it.id }
        if (_selectedChildId.value !in _children.value.map { it.id }) {
            _children.value.firstOrNull()?.id?.let { _selectedChildId.value = it }
        }
    }

    fun select(childId: String) {
        val normalized = childId.trim()
        if (normalized.isNotBlank()) _selectedChildId.value = normalized
    }

    fun clear() {
        _children.value = emptyList()
        _selectedChildId.value = ""
    }
}
